// UserPurchaseCourseList.jsx
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronLeft, faChevronRight, faSearch, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { Card, Table, InputGroup, Form, Modal, Button, Badge, Spinner } from "@themesberg/react-bootstrap";

import { adminAllCourseList, createCoursePaymentOrder, verifyCoursePayment, adminGetCourseById } from "../../../pages/api";
import { useNavigate } from "react-router-dom";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};


const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapCourseItem = (item) => ({
  id: item?.id ?? Date.now(),
  title: item?.title || "N/A",
  price: item?.price || "N/A",
  status: capitalize(item?.status || "N/A"),
  created_at: item?.created_at || "N/A",
  is_purchased: Boolean(item?.is_purchased),
  purchase_window_end: item?.purchase_window_end || "N/A",
  purchase_window_start: item?.purchase_window_start || "N/A",
  enrollment_start_date: item?.enrollment_start_date || "N/A",
  enrollment_end_date: item?.enrollment_end_date || "N/A",
});


const mapCourseBreakdown = (item, fallbackCourse = {}) => {
  const coursePrice = Number(
    item?.course_price ?? item?.price ?? fallbackCourse?.price ?? 0
  );
  const gstAmount = Number(
    item?.gst_amount ??
    item?.gst ??
    item?.gst_price ??
    0
  );
  const totalPrice = Number(
    item?.total_price ??
    item?.total ??
    coursePrice + gstAmount
  );

  return {
    id: item?.id ?? fallbackCourse?.id ?? Date.now(),
    title: item?.title ?? fallbackCourse?.title ?? "",
    course_price: coursePrice,
    gst_amount: gstAmount,
    total_price: totalPrice,
    gst_percentage: item?.gst_percentage ?? item?.gst_rate ?? null,
    raw: item,
  };
};

const getStatusBadge = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "success":
    case "active":
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "failed":
    case "rejected":
    case "inactive":
      return "danger";
    case "refunded":
      return "info";
    default:
      return "secondary";
  }
};

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay checkout.")));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay checkout."));
    document.body.appendChild(script);
  });

export default function UserPurchaseCourseList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purchasingId, setPurchasingId] = useState(null);
  const searchDebounceRef = useRef(null);
  const initialLoadRef = useRef(true);


  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseBreakdown, setCourseBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchCourses = async (page = 1, perPage = 10, searchTerm = "") => {
    try {
      setLoading(true);

      const params = {
        page,
        per_page: perPage,
      };

      const trimmedSearch = String(searchTerm || "").trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await adminAllCourseList(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCourses = Array.isArray(apiData?.courses) ? apiData.courses : [];
      const apiPagination = apiData?.pagination || {};

      setCourses(apiCourses.map(mapCourseItem));
      setPagination({
        current_page: apiPagination.current_page ?? 1,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? 10,
        total: apiPagination.total ?? apiCourses.length,
      });
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch course list.");
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchCourses(1, pagination.per_page, "");
      return;
    }

    clearSearchDebounce();
    searchDebounceRef.current = setTimeout(() => {
      fetchCourses(1, pagination.per_page, search);
    }, 300);

    return () => {
      clearSearchDebounce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filteredData = courses;

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Start pages
    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    // End pages
    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    // Middle pages: 1 ... previous current next ... last
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

   const openPurchaseModal = async (course) => {
    if (course?.is_purchased) return;

    try {
      setSelectedCourse(course);
      setCourseBreakdown(null);
      setShowPurchaseModal(true);
      setBreakdownLoading(true);

      const response = await adminGetCourseById(course.id);

      const responseData = response?.data || {};
      const apiData = responseData?.data || responseData?.course || responseData;

      setCourseBreakdown(mapCourseBreakdown(apiData, course));
    } catch (error) {
      console.error("Failed to fetch course breakdown:", error);
      toast.error(
        error?.response?.data?.message || "Unable to fetch course breakdown."
      );

      setCourseBreakdown(
        mapCourseBreakdown(
          {
            course_price: course?.price,
            gst_amount: 0,
            total_price: Number(course?.price || 0),
          },
          course
        )
      );
    } finally {
      setBreakdownLoading(false);
    }
  };

  const closePurchaseModal = () => {
    if (purchasingId) return;
    setShowPurchaseModal(false);
    setSelectedCourse(null);
    setCourseBreakdown(null);
    setBreakdownLoading(false);
  };


  const handlePurchase = async (course) => {
    if (course?.is_purchased) return;

    try {
      setPurchasingId(course.id);

      const orderResponse = await createCoursePaymentOrder({
        course_id: course.id,
      });

      const orderData = orderResponse?.data?.data;

      if (!orderData?.key_id || !(orderData?.order_id || orderData?.razorpay_order_id)) {
        throw new Error("Invalid payment order response.");
      }

      const razorpayOrderId = orderData.order_id || orderData.razorpay_order_id;

      await loadRazorpayScript();

      await new Promise((resolve, reject) => {
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency || "INR",
          name: "Life Skill Global",
          description: orderData.course_title || course.title,
          order_id: razorpayOrderId,
          prefill: orderData.prefill || {},
          theme: {
            color: "#1D4E89",
          },
          handler: async (paymentResponse) => {
            try {
              const verifyResponse = await verifyCoursePayment({
                transaction_id: orderData.transaction_id,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              });

              toast.success(
                verifyResponse?.data?.message ||
                  `Payment successful for "${course.title}".`
              );
              setShowPurchaseModal(false);
              setSelectedCourse(null);
              setCourseBreakdown(null);
              setBreakdownLoading(false);
              await fetchCourses(pagination.current_page, pagination.per_page, search);
              resolve();
            } catch (verifyError) {
              console.error("Payment verification failed:", verifyError);
              toast.error(
                verifyError?.response?.data?.message ||
                  "Payment verification failed."
              );
              reject(verifyError);
            }
          },
          modal: {
            ondismiss: () => {
              toast.info("Payment cancelled.");
              resolve();
            },
          },
        };

        const razorpay = new window.Razorpay(options);

        razorpay.on("payment.failed", (response) => {
          console.error("Razorpay payment failed:", response);
          toast.error(
            response?.error?.description ||
              response?.error?.reason ||
              "Payment failed. Please try again."
          );
          resolve();
        });

        razorpay.open();
      });
    } catch (error) {
      console.error("Course purchase failed:", error);
      toast.error(error?.response?.data?.message || error?.message || "Unable to start payment.");
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <>
      <div className="lsg-page">
      <div className="course-header py-2 mb-3">
        <div className="course-header-title">
          <h2 className="lsg-page-title">Purchase Courses</h2>
          <p className="lsg-page-subtitle">Browse available courses and complete payment securely.</p>
        </div>

        <div className="course-header-actions">
          <InputGroup className="course-search">
            <InputGroup.Text className="bg-white">
              <FontAwesomeIcon icon={faSearch} className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </InputGroup>

          <Button
            type="button"
            variant="outline-primary"
            onClick={() => navigate(-1)}
            className="admin-back-btn"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="course-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Purchase Course List</h5>
              {/* <div className="text-muted small">Latest course entries appear on top</div> */}
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr No</th>
                  <th>Course Title</th>
                  <th>Price</th>
                  <th className="text-center">Enrollment Start Date</th>
                  <th className="text-center">Enrollment End Date</th>
                  {/* <th className="text-center">Status</th> */}
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      Loading courses...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item, index) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{start + index}</td>
                      <td>{item.title}</td>
                      <td>₹{Number(item.price || 0).toLocaleString("en-IN")}</td>
                      {/* <td className="text-center">
                        <Badge
                          bg={getStatusBadge(item.status)}
                          className="px-3 py-2 rounded-pill"
                        >
                          {item.status}
                        </Badge>
                      </td> */}
                      <td className="text-center">{item.enrollment_start_date}</td>
                      <td className="text-center">{item.enrollment_end_date}</td>
                      <td className="text-center">
                        <div
                          // || item.status === "Inactive"
                          style={
                            item.is_purchased
                              ? { cursor: "not-allowed", display: "inline-block" }
                              : { display: "inline-block" }
                          }
                        >
                          <Button
                            type="button"
                            className="fw-semibold px-3 py-2"
                            style={{
                              ...(item.is_purchased
                                ? { ...ACTIVE_BUTTON_STYLE, opacity: 0.75 }
                                : ACTIVE_BUTTON_STYLE),
                              minWidth: "8.5rem",
                              whiteSpace: "nowrap",
                            }}
                            // onClick={() => openPurchaseModal(item)}
                            onClick={() => handlePurchase(item)}
                            disabled={purchasingId === item.id || item.is_purchased}
                          >
                            <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                            {item.is_purchased
                              ? "Purchased"
                              : purchasingId === item.id
                                ? "Purchasing..."
                                : "Purchase"}

                            {/* {item.is_purchased
                              ? "Purchased"
                              : item.status === "Purchase"
                                ? "Inactive"
                                : purchasingId === item.id
                                  ? "Purchasing..."
                                  : "Purchase"} */}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      No courses found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchCourses(Math.max(1, pagination.current_page - 1), pagination.per_page, search);
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() => {
                      clearSearchDebounce();
                      fetchCourses(item, pagination.per_page, search);
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() => {
                  clearSearchDebounce();
                  fetchCourses(Math.min(pagination.last_page, pagination.current_page + 1), pagination.per_page, search);
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showPurchaseModal}
        onHide={closePurchaseModal}
        centered
        backdrop={purchasingId ? "static" : true}
        keyboard={!purchasingId}
        size="md"
      >
        <Modal.Header closeButton={!purchasingId}>
          <Modal.Title className="fw-semibold">
            {selectedCourse?.title || "Course Purchase"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {breakdownLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" role="status" />
              <div className="text-muted mt-3">
                {/* Loading course breakdown... */}
                Loading course...
                </div>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <div className="text-muted small mb-2" style={{ lineHeight: "1.8" }}>
                  Please review the price before proceeding with the purchase.
                  {/* Please review the following price breakdown before proceeding with the purchase. */}
                </div>
                {/* <h5 className="mb-0">{courseBreakdown?.title || selectedCourse?.title}</h5> */}
              </div>

              <div className="rounded-4 p-4 bg-light">
                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">Course Price</span>
                  <span className="fw-semibold fs-5">
                    ₹{Number(courseBreakdown?.course_price || 0).toLocaleString("en-IN")}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">GST</span>
                  <span className="fw-semibold fs-5">
                    ₹{Number(courseBreakdown?.gst_amount || 0).toLocaleString("en-IN")}
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="text-muted">Total Price</span>
                  <span className="fw-bold fs-5">
                    ₹{Number(courseBreakdown?.total_price || 0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            // variant="outline-secondary"
            onClick={closePurchaseModal}
            disabled={purchasingId}
          >
            Cancel
          </Button>

          <Button
            type="button"
            className="fw-semibold px-4"
            style={ACTIVE_BUTTON_STYLE}
            onClick={() => handlePurchase(selectedCourse)}
            disabled={
              breakdownLoading ||
              purchasingId === selectedCourse?.id ||
              selectedCourse?.is_purchased
            }
          >
            <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
            {purchasingId === selectedCourse?.id ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                  role="status"
                />
                Purchasing...
              </>
            ) : selectedCourse?.is_purchased ? (
              "Purchased"
            ) : (
              "Purchase"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </div>

      <style>{`
        .course-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .course-header-title {
          flex: 0 0 auto;
        }

        .course-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex: 1;
        }

        .course-search {
          width: 100%;
          max-width: 300px;
          flex: 0 1 360px;
        }

        /* Mobile */
        @media (max-width: 767.98px) {
          .course-header {
            flex-direction: column;
            align-items: stretch;
          }

          .course-header-title {
            width: 100%;
          }

          .course-header-actions {
            width: 100%;
            // flex-direction: column;
            // align-items: stretch;
          }

          .course-search {
            width: 100%;
            max-width: none;
            flex: 1 1 auto;
          }

          .course-list-top {
            display: flex;
            flex-direction: row !important;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: nowrap;
          }

          .course-list-actions {
            display: flex;
            flex-direction: row !important;
            gap: 8px;
            flex-wrap: nowrap;
            margin-left: auto;
          }

          .course-list-actions .btn {
            width: auto !important;
            white-space: nowrap;
            padding: 6px 12px;
            font-size: 14px;
          }

          .table-responsive .btn {
            white-space: nowrap;
          }
        }
      `}</style>
    </>
  );
}